import React, { useState } from 'react';
import { Grid, TextField, Box, Snackbar, Fade } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Title from './Title';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { setDID, setDIDDocument, _did, _didDoc } from '../redux/offchainDIDResolverSlice';
import Spinner from './Spinner';
import { WizardButton } from './ui/WizardButton';
import JsonViewer from './ui/JsonViwer';
import useClipboard from './hooks/useClipboard';
import PageviewOutlinedIcon from '@material-ui/icons/PageviewOutlined';
import { trackEvent } from '../utils/google-analytics';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            '& > * + *': {
                marginTop: theme.spacing(2)
            }
        },
        btn: {
            marginBottom: 10
        }
    })
);

export default function OCDIDResolver() {
    const did = useSelector(_did);
    const didDoc = useSelector(_didDoc);
    const [isValidDID, setIsValidDID] = useState(true);
    const classes = useStyles();
    const { copyToClipboard, handleClose, snackBarState } = useClipboard();
    const [isResolving, setIsResolving] = useState(false);

    const dispatch = useDispatch();

    function getDID() {
        setIsResolving(true);
        axios
            .get(`${process.env.REACT_APP_DID_RESOLVER_URL}/did/key/${did}`)
            .then((res: any) => {
                setIsValidDID(true);
                dispatch(setDIDDocument(JSON.stringify(res.data.didDocument, null, 4)));
                setIsResolving(false);
                trackEvent('DID Tools', 'offchain_did_resolved', 'resolve_offchain_did');
            })
            .catch((err) => {
                if (err.response && err.response.data.error) {
                    dispatch(setDIDDocument(err.response.data.error));
                } else {
                    dispatch(setDIDDocument('Error'));
                }
                setIsResolving(false);
            });
    }

    function handleDidInput(did: string): void {
        dispatch(setDID(did));
    }

    return (
        <div>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        style={{ gap: 16 }}>
                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 10,
                                width: 38,
                                height: 38,
                                backgroundColor: '#2ec4b6',
                                color: '#fff'
                            }}>
                            <PageviewOutlinedIcon style={{ color: '#ffffff' }} />
                        </Box>
                        <Title>{'Resolve Offchain DID'}</Title>
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <div
                        style={{
                            border: '1px solid #2ec4b6',
                            borderRadius: 12,
                            padding: 16,
                            backgroundColor: '#fff'
                        }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    id="did"
                                    label="Decentralized Identity (DID)"
                                    fullWidth
                                    variant="outlined"
                                    inputProps={{ 'aria-label': 'description' }}
                                    placeholder="Paste a DID here to resolve its document..."
                                    value={did}
                                    onChange={(e: any) => handleDidInput(e.target.value)}
                                    error={!isValidDID}
                                    helperText={isValidDID ? '' : 'Invalid DID'}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <WizardButton
                                    disabled={did === '' ? true : false}
                                    className={classes.btn}
                                    onClick={getDID}
                                    variant="contained"
                                    style={{ textTransform: 'uppercase' }}
                                    endIcon={<PageviewOutlinedIcon fontSize="large" />}>
                                    Resolve DID
                                </WizardButton>
                                {didDoc && (
                                    <Box>
                                        <JsonViewer
                                            content={didDoc}
                                            header="DID Document"
                                            onCopy={copyToClipboard}
                                        />
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </div>
                </Grid>
                <Snackbar
                    open={snackBarState.open}
                    onClose={handleClose}
                    TransitionComponent={Fade}
                    message={snackBarState.text}
                    autoHideDuration={5000}
                />
            </Grid>
            {isResolving && <Spinner />}
        </div>
    );
}
